import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardContent, Typography, Grid, Box } from "@mui/material";
import {
  ManageAccounts as ManageIcon,
  SelfImprovement as MeditationIcon,
  School as TrainingIcon,
  Image as ImageIcon,
  Article as ContentIcon,
  Explore as VastuIcon,
  CardGiftcard as GiftIcon,
  Groups as CommunityIcon,
  Discount as DiscountIcon,
  Feedback as FeedbackIcon,
  Healing as RemedyIcon,
  LocalOffer as OffersIcon,
  MusicNote as MusicIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as PurchaseAnalyticsIcon,
  WorkspacePremium as SkillsIcon,
} from "@mui/icons-material";
import {
  meditation,
  training,
  image,
  cms,
  vastuCompass,
  gift,
  community,
  discount,
  feedback,
  concern,
  offers,
  ecommerce,
  music,
  purchaseAnalytics,
} from "../../routes/route";

const Management = () => {
  const navigate = useNavigate();
  const { rightsInfo, role } = useSelector((state) => state?.vedicRights);

  const managementItems = [
    {
      id: 1,
      title: "Music Management",
      icon: <MusicIcon sx={{ fontSize: 40, color: "#1976d2" }} />,
      path: music.MUSIC_LIST,
      description: "General management tools and settings",
      requiredRight: "music-management",
    },
    {
      id: 2,
      title: "Meditation Management",
      icon: <MeditationIcon sx={{ fontSize: 40, color: "#2e7d32" }} />,
      path: meditation.MEDITATION_LIST,
      description: "Manage meditation content and categories",
      requiredRight: "meditation-management",
    },
    {
      id: 3,
      title: "Training Management",
      icon: <TrainingIcon sx={{ fontSize: 40, color: "#ed6c02" }} />,
      path: training.TRAINING_LIST,
      description: "Manage training programs and materials",
      requiredRight: "training-management",
    },
    {
      id: 4,
      title: "Image Management",
      icon: <ImageIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      path: image.IMAGE_LIST,
      description: "Manage and approve images",
      requiredRight: "gallery-management",
    },
    {
      id: 5,
      title: "Content Management",
      icon: <ContentIcon sx={{ fontSize: 40, color: "#d32f2f" }} />,
      path: cms.CONTENT_MANAGEMENT,
      description: "Manage website content and CMS",
      requiredRight: "content-management",
    },
    {
      id: 6,
      title: "Vastu Compass Category",
      icon: <VastuIcon sx={{ fontSize: 40, color: "#5d4037" }} />,
      path: vastuCompass.VASTU_COMPASS_CATEGORY,
      description: "Manage Vastu compass categories",
      requiredRight: "vastu-management",
    },
    {
      id: 7,
      title: "Gift Management",
      icon: <GiftIcon sx={{ fontSize: 40, color: "#e91e63" }} />,
      path: gift.GIFT_MANAGEMENT,
      description: "Manage gift items and rewards",
      requiredRight: "gift-management",
    },
    {
      id: 8,
      title: "Community Management",
      icon: <CommunityIcon sx={{ fontSize: 40, color: "#00acc1" }} />,
      path: community.COMMUNITY_MANAGEMENT,
      description: "Manage community groups and interactions",
      requiredRight: "community-management",
    },
    {
      id: 9,
      title: "Discount Management",
      icon: <DiscountIcon sx={{ fontSize: 40, color: "#4caf50" }} />,
      path: discount.DISCOUNT_MANAGEMENT,
      description: "Manage discount codes and offers",
      requiredRight: "discount-management",
    },
    {
      id: 10,
      title: "Feedback Management",
      icon: <FeedbackIcon sx={{ fontSize: 40, color: "#ff9800" }} />,
      path: feedback.FEEDBACK_MANAGEMENT,
      description: "Manage user feedback and reviews",
      requiredRight: "feedback-management",
    },
    {
      id: 11,
      title: "Concern 4 Remedy",
      icon: <RemedyIcon sx={{ fontSize: 40, color: "#795548" }} />,
      path: concern.CONCERN,
      description: "Manage concerns and remedies",
      requiredRight: "concern-management",
    },
    {
      id: 12,
      title: "Offers Management",
      icon: <OffersIcon sx={{ fontSize: 40, color: "#f44336" }} />,
      path: offers.OFFERS,
      description: "Manage special offers and promotions",
      requiredRight: "offers-management",
    },
    {
      id: 13,
      title: "Ecommerce Management",
      icon: <StorefrontIcon sx={{ fontSize: 40, color: "#8b1e1e" }} />,
      path: ecommerce.ECOMMERCE,
      description: "Manage products, banners, offers, and coupons",
      requiredRight: "ecommerce-management",
    },
    {
      id: 14,
      title: "Purchase Analytics",
      icon: <PurchaseAnalyticsIcon sx={{ fontSize: 40, color: "#009688" }} />,
      path: purchaseAnalytics.PURCHASE_ANALYTICS,
      description: "Track first-time and repeat purchase analytics",
      requiredRight: "purchase-analytics-management",
    },
    {
      id: 15,
      title: "Skills Management",
      icon: <SkillsIcon sx={{ fontSize: 40, color: "#673ab7" }} />,
      path: `${cms.CONTENT_MANAGEMENT}?type=cons&contentType=skills`,
      description: "Manage consultant and user skills",
      requiredRight: "content-management",
    }
  ];

  const visibleManagementItems = managementItems.filter(
    (item) =>
      role === "admin" ||
      rightsInfo?.[item.requiredRight] === true
  );

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <div className="WrapperArea">
                <div className="WrapperBox">
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 'bold', color: '#1976d2' }}>
        Management Dashboard
      </Typography>
      
      <Grid container spacing={3}>
        {visibleManagementItems.map((item) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
                border: '1px solid #e0e0e0',
                borderRadius: 2,
              }}
              onClick={() => handleCardClick(item.path)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Box sx={{ mb: 2 }}>
                  {item.icon}
                </Box>
                <Typography 
                  variant="h6" 
                  component="h2" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#333',
                    fontSize: '1.1rem',
                    lineHeight: 1.3
                  }}
                >
                  {item.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.9rem',
                    lineHeight: 1.4
                  }}
                >
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
    </div>
    </div>
  );
};

export default Management;
